/**
 * Copyright (c) Riskaware 2017
 */

import geojsonArea from '@mapbox/geojson-area';
import L from 'leaflet';
import { Component } from 'react';

const geoOptions = {
    timeout: 10 * 1000, //Set a time out for 10s
    enableHighAccuracy: true // It's slower to resolve and uses more battery - but fine-grained
}

/*
 *  @class
 *  @classdesc Fetch data utility commands
 */
export class DataUtil extends Component {

    /**
     * Capitalises the first letter of the provided string and lower cases the rest of it
     * @param {String} str 
     */
    static capitaliseFirstLetter(str) {
        var strLowerCase = str.toLowerCase();
        return strLowerCase.charAt(0).toUpperCase() + strLowerCase.slice(1);
    };

    static getArea(geometry, radius) {
        var area;
        if (radius) {
            //This is circle -
            area = Math.pow(radius, 2) * Math.PI;
        }
        else if (geometry.type === "Point" || geometry.type === "LineString") {
            return null;
        } else {
            area = geojsonArea.geometry(geometry);
        }
        return Number(area).toFixed(2);
    }

    static boundsToGeoJson(bounds) {
        var nw = bounds.getNorthWest();
        var ne = bounds.getNorthEast();
        var se = bounds.getSouthEast();
        var sw = bounds.getSouthWest();

        return new L.Polygon([[nw.lat, nw.lng], [ne.lat, ne.lng], [se.lat, se.lng], [sw.lat, sw.lng]]).toGeoJSON();
    }

    static boundsToOverpassBoundsString(bounds) {
        var ne = bounds.getNorthEast();
        var sw = bounds.getSouthWest();

        return sw.lat + "," + sw.lng + "," + ne.lat + "," + ne.lng;
    }

    static incidentsEqual(incidentA, incidentB) {
        if (incidentA === null) {
            if (incidentB === null) {
                return true;
            }
            return false;
        }
        if (incidentB == null) {
            return false;
        }
        if (incidentA.id === incidentB.id) {
            return true;
        }
        return false;
    }

    /**
     * Returns formatted date without milliseconds in format DD/MM/YYYY HH:mm
     * @param {Date} date 
     */
    static formatDate(date) {
        var hours = date.getHours();
        var minutes = date.getMinutes();
        var day = date.getDate();
        var month = date.getMonth();
        hours = hours < 10 ? '0' + hours : hours;
        minutes = minutes < 10 ? '0' + minutes : minutes;
        day = day < 10 ? '0' + day : day;
        month = month + 1;
        month = month < 10 ? '0' + month : month;
        var strTime = hours + ':' + minutes;
        return day + "/" + month + "/" + date.getFullYear() + ", " + strTime;
    }

    /**
     * Returns formatted date without milliseconds in format DD/MM/YYYY HH:mm
     * @param {Date} date 
     */
    static formatTimelineDate(date) {
        var hours = date.getHours();
        var minutes = date.getMinutes();
        var day = date.getDate();
        var month = date.getMonth();
        hours = hours < 10 ? '0' + hours : hours;
        minutes = minutes < 10 ? '0' + minutes : minutes;
        day = day < 10 ? '0' + day : day;
        month = month + 1;
        month = month < 10 ? '0' + month : month;
        var strTime = hours + ':' + minutes;
        return strTime + " " + day + "/" + month + "/" + date.getFullYear();
    }

    /**
     * Returns formatted date without milliseconds in format DD/MM/YYYY HH:mm
     * @param {Date} date 
     */
    static formatTime(date) {
        var hours = date.getHours();
        var minutes = date.getMinutes();
        hours = hours < 10 ? '0' + hours : hours;
        minutes = minutes < 10 ? '0' + minutes : minutes;
        var strTime = hours + ':' + minutes;
        return strTime;
    }

    static calculateTimeBetween(startDate, endDate) {
        var time = (endDate.getTime() - startDate.getTime()) / 1000;
        return this.secondsToDurationString(time);
    }

    static secondsToDurationString(seconds) {
        var time = seconds;
        var hours = Math.floor(time / 3600);
        time = time - hours * 3600;

        var minutes = Math.floor(time / 60);
        var seconds = Math.floor(time - minutes * 60);

        return hours + ":" + (minutes < 10 ? ("0" + minutes) : minutes) + ":" + (seconds < 10 ? ("0" + seconds) : seconds);
    }

    /**
     * From https://developers.google.com/web/fundamentals/native-hardware/user-location/
     * TODO: request fine-grained location and set a timeout
     */
    static getLocation(updateLocationFunction) {
        if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(updateLocationFunction, (err) => {
                console.error("ERROR geolocating: ", err)
            },
                geoOptions);
        } else {
            console.error('Geolocation is not supported for this Browser/OS.');
        }
    }

    /**
     * From https://developers.google.com/web/fundamentals/native-hardware/user-location/
     * @param {function} updateLocationFunction 
     */
    static watchLocation(updateLocationFunction) {
        if (navigator.geolocation) {
            navigator.geolocation.watchPosition((position) => {
                updateLocationFunction(position.coords.latitude, position.coords.longitude);
            }, (err) => {
                console.error("ERROR geolocating: ", err)
            },
                geoOptions);
        } else {
            console.error('Geolocation is not supported for this Browser/OS.');
        }
    }

    static toRad(n) {
        return n * Math.PI / 180;
    }

    static toDeg(n) {
        return n * 180 / Math.PI;
    }

    static destVincenty(lat1, lon1, brng, dist) {
        var a = 6378137,
            b = 6356752.3142,
            f = 1 / 298.257223563, // WGS-84 ellipsiod
            s = dist,
            alpha1 = this.toRad(brng),
            sinAlpha1 = Math.sin(alpha1),
            cosAlpha1 = Math.cos(alpha1),
            tanU1 = (1 - f) * Math.tan(this.toRad(lat1)),
            cosU1 = 1 / Math.sqrt((1 + tanU1 * tanU1)), sinU1 = tanU1 * cosU1,
            sigma1 = Math.atan2(tanU1, cosAlpha1),
            sinAlpha = cosU1 * sinAlpha1,
            cosSqAlpha = 1 - sinAlpha * sinAlpha,
            uSq = cosSqAlpha * (a * a - b * b) / (b * b),
            A = 1 + uSq / 16384 * (4096 + uSq * (-768 + uSq * (320 - 175 * uSq))),
            B = uSq / 1024 * (256 + uSq * (-128 + uSq * (74 - 47 * uSq))),
            sigma = s / (b * A),
            sigmaP = 2 * Math.PI;
        while (Math.abs(sigma - sigmaP) > 1e-12) {
            var cos2SigmaM = Math.cos(2 * sigma1 + sigma),
                sinSigma = Math.sin(sigma),
                cosSigma = Math.cos(sigma),
                deltaSigma = B * sinSigma * (cos2SigmaM + B / 4 * (cosSigma * (-1 + 2 * cos2SigmaM * cos2SigmaM) - B / 6 * cos2SigmaM * (-3 + 4 * sinSigma * sinSigma) * (-3 + 4 * cos2SigmaM * cos2SigmaM)));
            sigmaP = sigma;
            sigma = s / (b * A) + deltaSigma;
        };
        var tmp = sinU1 * sinSigma - cosU1 * cosSigma * cosAlpha1,
            lat2 = Math.atan2(sinU1 * cosSigma + cosU1 * sinSigma * cosAlpha1, (1 - f) * Math.sqrt(sinAlpha * sinAlpha + tmp * tmp)),
            lambda = Math.atan2(sinSigma * sinAlpha1, cosU1 * cosSigma - sinU1 * sinSigma * cosAlpha1),
            C = f / 16 * cosSqAlpha * (4 + f * (4 - 3 * cosSqAlpha)),
            L = lambda - (1 - C) * f * sinAlpha * (sigma + C * sinSigma * (cos2SigmaM + C * cosSigma * (-1 + 2 * cos2SigmaM * cos2SigmaM))),
            revAz = Math.atan2(sinAlpha, -tmp); // final bearing
        return [this.toDeg(lat2), lon1 + this.toDeg(L)];
    };

    static distance(lat1, lon1, lat2, lon2) {
        var p = 0.017453292519943295;    // Math.PI / 180
        var c = Math.cos;
        var a = 0.5 - c((lat2 - lat1) * p) / 2 +
            c(lat1 * p) * c(lat2 * p) *
            (1 - c((lon2 - lon1) * p)) / 2;

        return 12742 * Math.asin(Math.sqrt(a)) * 1000; // 2 * R; R = 6371 km
    }

    static timeSince(date) {
        var seconds = Math.floor((new Date() - date) / 1000);

        var interval = Math.floor(seconds / 31536000);

        if (interval > 1) {
            return interval + " years";
        }
        interval = Math.floor(seconds / 2592000);
        if (interval > 1) {
            return interval + " months";
        }
        interval = Math.floor(seconds / 86400);
        if (interval > 1) {
            return interval + " days";
        }
        interval = Math.floor(seconds / 3600);
        if (interval > 1) {
            return interval + " hours";
        }
        interval = Math.floor(seconds / 60);
        if (interval > 1) {
            return interval + " minutes";
        }
        return Math.floor(seconds) + " seconds";
    }

    static addHeightToGeoJson(geoJson, height) {
        var updatedGeoJson = JSON.parse(JSON.stringify(geoJson));
        if (updatedGeoJson.geometry.type === 'Point') {
            updatedGeoJson.geometry.coordinates[2] = height;
        } else if (updatedGeoJson.geometry.type === 'LineString') {
            var i;
            for (i = 0; i < updatedGeoJson.geometry.coordinates.length; i++) {
                updatedGeoJson.geometry.coordinates[i][2] = height; // eslint-disable-line security/detect-object-injection
            }
        } else if (updatedGeoJson.geometry.type === 'Polygon') {
            var i;
            for (i = 0; i < updatedGeoJson.geometry.coordinates[0].length; i++) {
                updatedGeoJson.geometry.coordinates[0][i][2] = height; // eslint-disable-line security/detect-object-injection
            }
        }

        return updatedGeoJson;
    }

    static addHeightToLineGeoJson(geoJson, height, endHeight) {
        // Assume this is a line string type with 2 points and set start to height and end to endHeight
        var updatedGeoJson = JSON.parse(JSON.stringify(geoJson));
        updatedGeoJson.geometry.coordinates[0][2] = height;
        updatedGeoJson.geometry.coordinates[1][2] = endHeight;

        return updatedGeoJson;
    }

    static getLengthOfGeoJsonLine(geoJson) {
        if (geoJson.geometry.type === 'LineString') {
            var previousPoint = null;
            var length = 0;
            geoJson.geometry.coordinates.forEach((latLng) => {
                var lLatLon = L.latLng(latLng[1], latLng[0]);
                if (previousPoint !== null) {
                    length = length + Math.abs(previousPoint.distanceTo(lLatLon));
                }
                previousPoint = lLatLon;
            });
            return length;
        } else {
            return 1;
        }
    }

    static getGeoJsonLength(geoJson) {
        if (geoJson.properties && geoJson.properties.radius) {
            //This is circle -
            return geoJson.properties.radius * 2 * Math.PI;
        }
        else if (geoJson.geometry.type === "Point" || geoJson.geometry.type === "MultiPoint") {
            // Return 0 for points
            return 0;
        }
        else if (geoJson.geometry.type === "Polygon") {
            var previousPoint = null;
            var length = 0;
            geoJson.geometry.coordinates[0].forEach((latLng) => {
                var lLatLon = L.latLng(latLng[1], latLng[0]);
                if (previousPoint !== null) {
                    length = length + Math.abs(previousPoint.distanceTo(lLatLon));
                }
                previousPoint = lLatLon;
            });
            return length;
        } else {
            var previousPoint = null;
            var length = 0;
            geoJson.geometry.coordinates.forEach((latLng) => {
                var lLatLon = L.latLng(latLng[1], latLng[0]);
                if (previousPoint !== null) {
                    length = length + Math.abs(previousPoint.distanceTo(lLatLon));
                }
                previousPoint = lLatLon;
            });
            return length;
        }
    }
}